import {
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { ApplicationStateService } from '../../../services/application-state.service';
import { CommunicationService } from '../../../services/communication.service';
import { BookFeatureService } from '../../../services/book-feature.service';
import { PositioningService } from '../../../services/positioning.service';
import { PageChangedEvent } from '../../../utils/events/page-changed-event';
import { PageTextService } from '../../../services/page-text.service';
import { PageFavoriteService } from '../../../services/page-favorite.service';
import { PageService } from '../../../services/page.service';
import { PageEditModeFactory } from '../../../utils/page-edit-modes/page-edit-mode-factory';
import { ZoomService } from '../../../services/zoom.service';
import { environment } from '../../../../environments/environment';
import { PageEditMode } from '../../../utils/page-edit-modes/page-edit-mode';
import { SearchService } from '../../../services/search.service';
import { QuickLinkService } from '../../../services/quick-link.service';
import { EventUtils } from '../../../utils/event-utils';
import { PageBookFeatures } from '../../../models/page-book-features';
import { BookFeatureActivationService } from '../../../services/book-feature-activation.service';
import { QuickLinkFactory } from '../../../models/factories/quick-link-factory';
import { SidebarService } from '../../../services/sidebar.service';
import { AbstractPageComponent } from './abstract-page-component';
import { Subscription } from 'rxjs';
import { DictionaryService } from '../../../services/dictionary.service';
import { WhiteboardDrawingsService } from '@lemberger/whiteboards';
import { SearchResultRectComponent } from './search-result-rect/search-result-rect.component';
import { BookFeatureComponent } from './book-feature/book-feature.component';
import { ShareDropdownComponent } from './book-feature/share-dropdown/share-dropdown.component';
import { PageTurnerComponent } from '../../utils/page-turner/page-turner.component';
import { PageReadspeakerComponent } from './book-feature/page-readspeaker/page-readspeaker.component';
import {
    QuicklinkDropdownComponent
} from './book-feature/page-quick-link/quicklink-dropdown/quicklink-dropdown.component';
import { QuicklinkOverlayComponent } from './quicklink-overlay/quicklink-overlay.component';
import { PageFavoriteComponent } from './book-feature/page-favorite/page-favorite.component';
import { PageTextBoxesComponent } from './book-feature/page-text-boxes/page-text-boxes.component';
import { PageCanvasComponent } from './book-feature/page-canvas/page-canvas.component';
import { AsyncPipe, NgStyle } from '@angular/common';
import $ from 'jquery';

@Component({
    selector: 'app-page',
    templateUrl: './page.component.html',
    styleUrls: ['./page.component.scss'],
    standalone: true,
    imports: [
        SearchResultRectComponent,
        BookFeatureComponent,
        BookFeatureComponent,
        BookFeatureComponent,
        BookFeatureComponent,
        ShareDropdownComponent,
        PageTurnerComponent,
        PageReadspeakerComponent,
        QuicklinkDropdownComponent,
        QuicklinkOverlayComponent,
        PageFavoriteComponent,
        PageTextBoxesComponent,
        PageCanvasComponent,
        AsyncPipe,
        NgStyle
    ]
})
export class PageComponent extends AbstractPageComponent implements OnInit, OnDestroy, OnChanges {

    loading = true;
    isLeftPage: boolean;
    isRightPage: boolean;
    isCenterPage: boolean;
    showLeftFavorite: boolean;
    showRightFavorite: boolean;

    viewModeChangedSubscription: Subscription;
    popoverContainer: string;

    constructor(public pageFavoriteService: PageFavoriteService,
                public bookFeatureActivationService: BookFeatureActivationService,
                public pageService: PageService,
                public pageTextService: PageTextService,
                public applicationStateService: ApplicationStateService,
                protected communicationService: CommunicationService,
                protected positioningService: PositioningService,
                protected bookFeatureService: BookFeatureService,
                protected searchService: SearchService,
                protected quickLinkService: QuickLinkService,
                protected componentFactoryResolver: ComponentFactoryResolver,
                protected pageEditModeFactory: PageEditModeFactory,
                protected changeDetectorRef: ChangeDetectorRef,
                protected sidebarService: SidebarService,
                protected zoomService: ZoomService,
                protected dictionaryService: DictionaryService,
                protected quickLinkFactory: QuickLinkFactory,
                protected whiteboardService: WhiteboardDrawingsService) {
        super();
        this.initSubscriptions();
        this.viewModeChangedSubscription = this.communicationService.viewModeChangeAnnounced$.subscribe(() => {
            this.getPagePositionInViewMode();
        });
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.$container = $(this.container.nativeElement);
        this.imgSrc = this.page.image.url;
        this.getPagePositionInViewMode();
        this.bookFeatures = new PageBookFeatures();
        this.popoverContainer = `.bookviewer-single-page-container[data-id="${this.page.id}"]`;

        this.fetchCachedData()
            .then(() => {

                // On the first page fetch the data immediately
                if (this.page.index === 0) {
                    this.fetchSolutionQuickLinksAndDropdownPossition();
                    this.fetchTextData();

                    // On all other pages set a little timeout before loading data
                } else {

                    if (this.dataFetchingTimeoutId) {
                        clearTimeout(this.dataFetchingTimeoutId);
                    }

                    this.dataFetchingTimeoutId = setTimeout(() => {
                        this.fetchSolutionQuickLinksAndDropdownPossition();
                        this.fetchTextData();
                    }, 700);

                }
            });

        this.fetchSearchData();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.viewModeChangedSubscription.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateCanvas();
    }

    onDragStart($event): boolean {
        $event.preventDefault();
        $event.stopPropagation();

        return false;
    }

    onLoad(): void {
        if (!this.loading) {
            return;
        }

        this.loading = false;
        this.communicationService.announcePageSizeChanges();
    }

    onSwipe($event, direction: string): void {

        if (this.shouldPreventSwipe($event)) {
            return;
        }

        if (direction === 'forward') {
            this.communicationService.announcePageTurn(PageChangedEvent.createTurnPageForwardEvent());
        } else {
            this.communicationService.announcePageTurn(PageChangedEvent.createTurnPageBackwardsEvent());
        }
    }

    private shouldPreventSwipe($event): boolean {
        if (this.applicationStateService.preventSwipe) {
            return true;
        }

        const whiteListClasses = ['bookviewer-page', 'upper-canvas'];
        if (!(EventUtils.getEventPath($event.srcEvent)[0].className &&
            this.includesOneOf(EventUtils.getEventPath($event.srcEvent)[0].className, whiteListClasses))) {
            return true;
        }

        const clickedElement = $(document.elementFromPoint(
            $event.changedPointers[0].clientX - $event.deltaX,
            $event.changedPointers[0].clientY - $event.deltaY));

        if (clickedElement && !this.includesOneOf(clickedElement.attr('class'), whiteListClasses)) {
            return true;
        }

        const editMode: PageEditMode = this.applicationStateService.pageEditMode;

        return !!(this.applicationStateService.isPageEditable
            || this.applicationStateService.zoomLevel !== environment.minZoomLevel
            || editMode);
    }

    private getPagePositionInViewMode(): void {
        this.isLeftPage = this.pageService.isLeftPage(this.page);
        this.isRightPage = this.pageService.isRightPage(this.page);
        this.isCenterPage = this.pageService.isCenterPage(this.page);
        this.showLeftFavorite = this.pageFavoriteService.shouldShowLeftBookmark(this.page);
        this.showRightFavorite = this.pageFavoriteService.shouldShowRightBookmark(this.page);
    }

    private includesOneOf(value: string, searchStrings: Array<string>): boolean {

        for (const searchValue of searchStrings) {
            if (value && value.includes && value.includes(searchValue)) {
                return true;
            }
        }

        return false;
    }
}
